/**
 * Sub-release 2.6.1 + 2.6.23 — Wizard 3-step post attivazione Visto Verde
 *
 * Step 1: Anagrafica cliente (promuove Smart → Prospect, lock dopo prima conferma)
 * Step 2: Elenco documenti mirato per tipologia cliente
 * Step 3: Istruzioni invio documenti + email dedicata
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './ui/select';
import { CheckCircle2, User, ClipboardList, Mail, Loader2, ArrowLeft, ArrowRight, Copy, Lock, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { TIPOLOGIE_CLIENTE, DOCUMENTI_BASE, DOCUMENTI_PER_TIPOLOGIA, isPersonaFisica, isPersonaGiuridica, tipologiaLabel } from '../lib/tipologieCliente';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOCS_EMAIL = 'preventivi@machenoleggio.it';


const EMPTY_ANAGRAFICA = {
  nome_cognome: '', ragione_sociale: '', responsabile: '', telefono: '', email: '',
  tipologia_cliente: '', codice_fiscale: '', partita_iva: '',
  citta: '', indirizzo: '', cap: '', km_annui: 15000, convenzione_id: '', convenzione_nome: '',
};

export default function VistoVerdeWizard({ open, onClose, offer, onCompleted }) {
  const { getAuthHeader, user } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const [anagrafica, setAnagrafica] = useState(EMPTY_ANAGRAFICA);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/client/anagrafica`, { headers: getAuthHeader() });
        const d = res.data || {};
        setAnagrafica({
          nome_cognome: d.nome_cognome || user?.name || '',
          ragione_sociale: d.ragione_sociale || '',
          responsabile: d.responsabile || '',
          telefono: d.telefono || '',
          email: d.email || user?.email || '',
          tipologia_cliente: d.tipologia_cliente || '',
          codice_fiscale: d.codice_fiscale || '',
          partita_iva: d.partita_iva || '',
          citta: d.citta || '',
          indirizzo: d.indirizzo || '',
          cap: d.cap || '',
          km_annui: d.km_annui || 15000,
          convenzione_id: d.convenzione_id || '',
          convenzione_nome: d.convenzione_nome || '',
        });
        setLocked(!!d.locked);
      } catch {
        setAnagrafica(EMPTY_ANAGRAFICA);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, user, getAuthHeader]);

  const setField = (k, v) => setAnagrafica((prev) => ({ ...prev, [k]: v }));
  const isSocieta = isPersonaGiuridica(anagrafica.tipologia_cliente);
  const needsCF = isPersonaFisica(anagrafica.tipologia_cliente) || ['libero_professionista', 'ditta_individuale'].includes(anagrafica.tipologia_cliente);
  const needsPIVA = Boolean(anagrafica.tipologia_cliente) && !isPersonaFisica(anagrafica.tipologia_cliente);
  const docsTipologia = DOCUMENTI_PER_TIPOLOGIA[anagrafica.tipologia_cliente];

  const handleSaveStep1 = async () => {
    if (locked) { setStep(2); return; }
    const errors = [];
    if (!anagrafica.nome_cognome?.trim()) errors.push('Nome e Cognome / Ragione Sociale');
    if (!anagrafica.telefono?.trim()) errors.push('Telefono');
    if (!anagrafica.email?.trim()) errors.push('Email');
    if (!anagrafica.tipologia_cliente) errors.push('Tipologia Cliente');
    if (!anagrafica.citta?.trim()) errors.push('Città');
    if (!anagrafica.indirizzo?.trim()) errors.push('Indirizzo');
    if (!anagrafica.cap?.trim()) errors.push('CAP');
    if (isSocieta && !anagrafica.responsabile?.trim()) errors.push('Responsabile');
    if (!anagrafica.codice_fiscale?.trim() && !anagrafica.partita_iva?.trim()) {
      errors.push('Codice Fiscale o Partita IVA (almeno uno)');
    }
    if (errors.length) {
      toast.error(`Compila: ${errors.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/client/anagrafica`, anagrafica, { headers: getAuthHeader() });
      toast.success('Anagrafica salvata. Ora sei classificato come Prospect.');
      setLocked(true);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio dell'anagrafica");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => { if (typeof onClose === 'function') onClose(); };
  const veicolo = offer?.vehicle_info || {};
  const veicoloLabel = (() => {
    const base = [veicolo.marca, veicolo.modello].filter(Boolean).join(' ');
    const ver = veicolo.versione || '';
    return ver.toUpperCase().startsWith(base.toUpperCase()) ? ver : [base, ver].filter(Boolean).join(' ');
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="visto-verde-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            Visto Verde Attivato — {veicoloLabel || 'Preventivo'}
          </DialogTitle>
          <DialogDescription>
            L&apos;offerta è congelata per 48 ore. Completa i 3 step per finalizzare la pratica.
          </DialogDescription>
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex items-center gap-2 pt-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                  step === s ? 'bg-emerald-600 text-white border-emerald-600' :
                  step > s ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                  'bg-white text-slate-400 border-slate-200'
                }`}
                data-testid={`wizard-step-badge-${s}`}
              >
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`h-0.5 flex-1 ${step > s ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-500 flex justify-between px-1">
          <span className={step === 1 ? 'text-emerald-700 font-semibold' : ''}>Anagrafica</span>
          <span className={step === 2 ? 'text-emerald-700 font-semibold' : ''}>Documenti</span>
          <span className={step === 3 ? 'text-emerald-700 font-semibold' : ''}>Invio Documenti</span>
        </div>

        {/* STEP 1 - Anagrafica */}
        {step === 1 && (
          <div className="space-y-4 pt-2" data-testid="wizard-step-1">
            <div className="flex items-center gap-2 text-slate-700">
              <User className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold">Dati Anagrafici</h3>
              <Badge className="ml-auto bg-emerald-100 text-emerald-800 border border-emerald-300">Step 1/3</Badge>
            </div>

            {locked && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="anagrafica-locked-note">
                <Lock className="w-4 h-4 text-amber-700 mt-0.5" />
                <p className="text-sm text-amber-800">
                  <strong>Dati anagrafici già confermati.</strong> Per eventuali variazioni contatta il tuo agente di riferimento.
                </p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Nome e Cognome / Ragione Sociale *" value={anagrafica.nome_cognome}
                  onChange={(v) => setField('nome_cognome', v)} disabled={locked}
                  testid="an-nome-cognome" className="sm:col-span-2" />

                <FormField label="Telefono / Cellulare *" value={anagrafica.telefono}
                  onChange={(v) => setField('telefono', v)} disabled={locked} testid="an-tel" />
                <FormField label="Email *" value={anagrafica.email}
                  onChange={(v) => setField('email', v)} disabled={locked} testid="an-email" />

                {isSocieta && (
                  <FormField label="Responsabile (Nome e Cognome) *" value={anagrafica.responsabile}
                    onChange={(v) => setField('responsabile', v)} disabled={locked}
                    testid="an-responsabile" className="sm:col-span-2" />
                )}

                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs font-medium text-slate-700">Tipologia Cliente *</Label>
                  <Select value={anagrafica.tipologia_cliente || undefined}
                    onValueChange={(v) => setField('tipologia_cliente', v)} disabled={locked}>
                    <SelectTrigger className="h-9 text-sm" data-testid="an-tipologia">
                      <SelectValue placeholder="Seleziona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOLOGIE_CLIENTE.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <FormField label={`Codice Fiscale${needsCF ? ' *' : ''}`} value={anagrafica.codice_fiscale}
                  onChange={(v) => setField('codice_fiscale', (v || '').toUpperCase())} disabled={locked}
                  testid="an-cf" />
                <FormField label={`Partita IVA${needsPIVA ? ' *' : ''}`} value={anagrafica.partita_iva}
                  onChange={(v) => setField('partita_iva', v)} disabled={locked} testid="an-piva" />

                <FormField label="Città *" value={anagrafica.citta}
                  onChange={(v) => setField('citta', v)} disabled={locked} testid="an-citta" />
                <FormField label="CAP *" value={anagrafica.cap}
                  onChange={(v) => setField('cap', v)} disabled={locked} testid="an-cap" />
                <FormField label="Indirizzo *" value={anagrafica.indirizzo}
                  onChange={(v) => setField('indirizzo', v)} disabled={locked}
                  testid="an-indirizzo" className="sm:col-span-2" />

                <FormField label="Km annui previsti *" value={String(anagrafica.km_annui || 15000)}
                  onChange={(v) => setField('km_annui', Number(v) || 15000)} disabled testid="an-km" />
                <FormField label="Convenzione" value={anagrafica.convenzione_nome || '—'}
                  onChange={() => {}} disabled testid="an-conv" />
              </div>
            )}

            {!locked && !loading && (
              <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>Al salvataggio verrai promosso da Smart a Prospect. I dati saranno congelati e modificabili solo dal tuo agente.</span>
              </div>
            )}

            <div className="flex justify-between pt-3 border-t border-slate-100">
              <Button variant="outline" onClick={handleClose} data-testid="wizard-cancel">Chiudi</Button>
              <Button
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSaveStep1}
                disabled={saving || loading}
                data-testid="wizard-step1-next"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>{locked ? 'Prosegui' : 'Salva e Continua'} <ArrowRight className="w-4 h-4" /></>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 - Documentazione mirata per tipologia */}
        {step === 2 && (
          <div className="space-y-4 pt-2" data-testid="wizard-step-2">
            <div className="flex items-center gap-2 text-slate-700">
              <ClipboardList className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold">Documenti da preparare</h3>
              <Badge className="ml-auto bg-emerald-100 text-emerald-800 border border-emerald-300">Step 2/3</Badge>
            </div>
            <p className="text-sm text-slate-600">
              In base alla tipologia <strong data-testid="wizard-docs-tipologia">{tipologiaLabel(anagrafica.tipologia_cliente, '—')}</strong> servono questi documenti:
            </p>
            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="wizard-docs-base">
              <p className="text-xs uppercase font-semibold text-slate-500">Per tutti</p>
              <ul className="text-sm text-slate-700 space-y-1.5">
                {DOCUMENTI_BASE.map((d) => (
                  <li key={d} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{d}</li>
                ))}
              </ul>
            </div>
            {docsTipologia && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3" data-testid="wizard-docs-tipologia-list">
                <p className="text-xs uppercase font-semibold text-emerald-700">{tipologiaLabel(anagrafica.tipologia_cliente)}</p>
                <ul className="text-sm text-slate-700 space-y-1.5">
                  {docsTipologia.docs.map((d) => (
                    <li key={d} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{d}</li>
                  ))}
                </ul>
                {docsTipologia.nota && (
                  <p className="text-xs text-slate-600 italic" data-testid="wizard-docs-nota">Nota: {docsTipologia.nota}</p>
                )}
              </div>
            )}
            <div className="flex justify-between pt-3 border-t border-slate-100">
              <Button variant="outline" onClick={() => setStep(1)} className="gap-1.5" data-testid="wizard-step2-back">
                <ArrowLeft className="w-4 h-4" /> Indietro
              </Button>
              <Button className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setStep(3)} data-testid="wizard-step2-next">
                Continua <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 - Invio Documenti */}
        {step === 3 && (
          <div className="space-y-4 pt-2" data-testid="wizard-step-3">
            <div className="flex items-center gap-2 text-slate-700">
              <Mail className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold">Invio Documenti</h3>
              <Badge className="ml-auto bg-emerald-100 text-emerald-800 border border-emerald-300">Step 3/3</Badge>
            </div>
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 space-y-3">
              <p className="text-sm text-slate-700">
                Invia i documenti richiesti entro 48 ore per confermare il canone Target sbloccato.
              </p>
              <div className="pt-2 border-t border-emerald-200 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <div>
                  <p className="text-xs uppercase text-slate-500">Invia a:</p>
                  <p className="text-lg font-bold text-emerald-800">{DOCS_EMAIL}</p>
                </div>
                <Button size="sm" variant="outline" className="sm:ml-auto gap-1.5"
                  onClick={() => { navigator.clipboard.writeText(DOCS_EMAIL); toast.success('Email copiata'); }}
                  data-testid="wizard-copy-email">
                  <Copy className="w-4 h-4" /> Copia
                </Button>
                <a href={`mailto:${DOCS_EMAIL}?subject=Invio%20documenti%20preventivo%20${encodeURIComponent(veicoloLabel)}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                  data-testid="wizard-mailto-link">
                  <Mail className="w-4 h-4" /> Apri Email
                </a>
              </div>
            </div>
            <div className="flex justify-between pt-3 border-t border-slate-100">
              <Button variant="outline" onClick={() => setStep(2)} className="gap-1.5" data-testid="wizard-step3-back">
                <ArrowLeft className="w-4 h-4" /> Indietro
              </Button>
              <Button className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => { if (typeof onCompleted === 'function') onCompleted(); handleClose(); }}
                data-testid="wizard-complete">
                <CheckCircle2 className="w-4 h-4" /> Ho preso nota
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, value, onChange, type = 'text', testid, className = '', disabled = false }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-xs font-medium text-slate-700">{label}</Label>
      <Input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="h-9 text-sm disabled:bg-slate-100 disabled:text-slate-600 disabled:cursor-not-allowed"
        data-testid={testid}
      />
    </div>
  );
}
